import { useSelector } from 'react-redux';
import { List, ListItemButton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useToDoListItems } from './services/toDoListItems';
import { selectSubscriberDocuments } from './services';
import { Task } from './types';
import { BenefitsPageResponseDto, EoiContextsDto } from 'theSource/queries';
import {
  DueDateStyled,
  ListItemNavigateIconStyled,
  ListItemTextStyled,
  ToDoListItem,
} from './ToDoListStyled';
import { Card } from 'components';
import { collectCoveragesPendingEoi } from '../EOI/components';

interface ToDoListProps {
  benefitsData?: BenefitsPageResponseDto;
}

export const ToDoList = ({ benefitsData }: ToDoListProps) => {
  const navigate = useNavigate();
  const subscriberDocumentsState = useSelector(selectSubscriberDocuments);
  const { t } = useTranslation('pages.home');

  const pluginEoiData =
    benefitsData?.data.eoi ||
    ({ previous: {}, current: {}, next: {} } as EoiContextsDto);
  const coveragesPendingEoi = collectCoveragesPendingEoi(benefitsData?.data);

  const hasAnyCoverageWaitingForEoi = ['current', 'next', 'previous'].some(
    (item) =>
      Array.isArray(benefitsData?.data?.[item]) &&
      benefitsData.data[item].some(
        (benefit: any) => benefit.coverage && benefit.coverage.waiting_for_eoi
      )
  );

  const toDoListItems: Task[] = useToDoListItems(
    subscriberDocumentsState,
    hasAnyCoverageWaitingForEoi ? pluginEoiData : { previous: {}, current: {}, next: {} },
    coveragesPendingEoi,
    t
  );

  const handleTaskClick = (task: Task) => {
    if (!task) {
      return;
    }

    if (task.path) {
      navigate(task.path);
    }
  };

  return (
    toDoListItems.length > 0 && (
      <Card additionalClasses="w-100" showBorder>
        <div className="row">
          <div className="col">
            <h2>{t('toDoList')}</h2>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <p>{t('toDoListDescription')}</p>
          </div>
        </div>
        <List>
          {toDoListItems.map((task: Task) => {
            return (
              <ToDoListItem
                key={task.value}
                secondaryAction={<ListItemNavigateIconStyled />}
              >
                <ListItemButton onClick={() => handleTaskClick(task)}>
                  <ListItemTextStyled>
                    <Typography>{task.name}</Typography>
                    {task.secondaryText && (
                      <DueDateStyled>{task.secondaryText}</DueDateStyled>
                    )}
                  </ListItemTextStyled>
                </ListItemButton>
              </ToDoListItem>
            );
          })}
        </List>
      </Card>
    )
  );
};
